import React, { useState, useEffect, useRef } from 'react';
import api, { getImageUrl } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { FaTrash, FaCloudUploadAlt, FaCheckSquare, FaRegSquare, FaCheck, FaBan } from 'react-icons/fa';

export default function AdminReferences() {
  useAuth();
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchReferences();
  }, []);

  const fetchReferences = async () => {
    try {
      const res = await api.get('/references/all');
      setReferences(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Referanslar yuklenirken hata olustu');
    } finally {
      setLoading(false);
    }
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach((file) => {
        formData.append('images', file);
      });

      await api.post('/references', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${files.length} gorsel yuklendi`);
      fetchReferences();
    } catch (err) {
      toast.error('Yukleme sirasinda hata olustu');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    uploadFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu gorseli silmek istediginize emin misiniz?')) return;
    try {
      await api.delete(`/references/${id}`);
      toast.success('Gorsel silindi');
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      fetchReferences();
    } catch (err) {
      toast.error('Silme sirasinda hata olustu');
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === references.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(references.map((r) => r.id)));
    }
  };

  const handleBulkAction = async (action) => {
    if (selected.size === 0) return;
    const labels = {
      delete: 'silmek',
      activate: 'aktif yapmak',
      deactivate: 'pasif yapmak',
    };
    if (!window.confirm(`${selected.size} referansi ${labels[action]} istediginize emin misiniz?`)) return;
    try {
      await api.post('/references/bulk-action', {
        action,
        ids: Array.from(selected),
      });
      toast.success(`${selected.size} referans guncellendi`);
      setSelected(new Set());
      fetchReferences();
    } catch (err) {
      toast.error('Toplu islem sirasinda hata olustu');
    }
  };

  if (loading) {
    return <div className="admin-loading">Yukleniyor...</div>;
  }

  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Referans Yonetimi</h2>
        </div>

        {/* Upload Area */}
        <div
          className="upload-area"
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          style={{
            borderColor: dragOver ? '#86CD91' : '#ddd',
            background: dragOver ? 'rgba(134, 205, 145, 0.05)' : 'transparent',
            marginBottom: 25,
          }}
        >
          <FaCloudUploadAlt />
          <p>
            {uploading
              ? 'Yukleniyor...'
              : 'Gorselleri surukleyip birakin veya tiklayin'}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>

        {/* Bulk Actions */}
        {references.length > 0 && (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-sm btn-secondary"
              onClick={toggleSelectAll}
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              {selected.size === references.length ? <FaCheckSquare /> : <FaRegSquare />}
              {selected.size === references.length ? 'Secimi Kaldir' : 'Tumunu Sec'}
            </button>
            {selected.size > 0 && (
              <>
                <span style={{ fontSize: '13px', color: '#666' }}>{selected.size} secili</span>
                <button className="btn btn-sm btn-danger" onClick={() => handleBulkAction('delete')}>
                  <FaTrash /> Secilenleri Sil
                </button>
                <button className="btn btn-sm btn-primary" onClick={() => handleBulkAction('activate')}>
                  <FaCheck /> Aktif Yap
                </button>
                <button className="btn btn-sm btn-secondary" onClick={() => handleBulkAction('deactivate')}>
                  <FaBan /> Pasif Yap
                </button>
              </>
            )}
          </div>
        )}

        {/* Image Grid */}
        {references.length === 0 ? (
          <div className="empty-state">
            <FaCloudUploadAlt />
            <p>Henuz referans gorseli eklenmemis</p>
          </div>
        ) : (
          <div className="image-grid">
            {references.map((ref) => (
              <div
                className="image-grid-item"
                key={ref.id}
                style={{
                  outline: selected.has(ref.id) ? '3px solid #86CD91' : 'none',
                  borderRadius: '8px',
                  position: 'relative',
                  opacity: ref.is_active === false ? 0.5 : 1,
                }}
              >
                {/* Checkbox */}
                <div
                  onClick={(e) => { e.stopPropagation(); toggleSelect(ref.id); }}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    zIndex: 3,
                    background: 'rgba(255,255,255,0.9)',
                    borderRadius: '4px',
                    padding: '2px 4px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: selected.has(ref.id) ? '#86CD91' : '#999',
                  }}
                >
                  {selected.has(ref.id) ? <FaCheckSquare /> : <FaRegSquare />}
                </div>

                {/* Active/Inactive badge */}
                {ref.is_active === false && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    zIndex: 3,
                    background: 'rgba(220,53,69,0.85)',
                    color: '#fff',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '11px',
                  }}>
                    Pasif
                  </div>
                )}

                <img src={getImageUrl(ref.image_path)} alt={ref.title || 'Referans'} />
                <div className="overlay">
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(ref.id)}
                  >
                    <FaTrash /> Sil
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
